import { useEffect, useState, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ClipboardList, Send, Package, Save, Plus, X, User, Trash2, CheckCircle2, ArrowLeft, ArrowRight, ShieldCheck, Check, Repeat, Calendar } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'

// Effective value of a card_product = override if set, else product field.
function effective(cp, field) {
  if (cp.overrides && Object.prototype.hasOwnProperty.call(cp.overrides, field)) {
    return cp.overrides[field]
  }
  return cp.products?.[field]
}

function snapshotFromCardProduct(cp) {
  const p = cp.products || {}
  return {
    card_product_id: cp.id,
    product_id: p.id,
    sku_at_order: p.sku,
    name_at_order: effective(cp, 'name'),
    description_at_order: effective(cp, 'description'),
    unit_at_order: effective(cp, 'unit'),
    pack_size_at_order: effective(cp, 'pack_size') ?? null,
    price_at_order: Number(effective(cp, 'price') ?? 0),
    image_url: p.image_url,
  }
}

const newConsignment = (tempId) => ({
  tempId,
  recipient_name: '',
  recipient_ref: '',
  lines: [],
})

export default function PlaceOrder({ activeCompanyId, previewMode = false }) {
  const navigate = useNavigate()
  const [cards, setCards] = useState([])
  const [selectedCardId, setSelectedCardId] = useState(null)
  const [items, setItems] = useState([])
  const [company, setCompany] = useState(null)
  const [customerPo, setCustomerPo] = useState('')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [clientNotes, setClientNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)

  const [consignments, setConsignments] = useState([newConsignment('c-1')])
  const [pickingForId, setPickingForId] = useState(null) // consignment tempId that's currently picking
  const [pickerSearch, setPickerSearch] = useState('')
  const [mode, setMode] = useState('building')   // 'building' | 'review'
  // Wizard step: 1=pick card, 2=add products, 3=delivery details, 4=review
  const [step, setStep] = useState(1)
  // Recent orders shown on Step 1 as a "Duplicate a previous order" panel.
  const [pastOrders, setPastOrders] = useState([])
  const [loadingPrev, setLoadingPrev] = useState(false)
  const tempIdCounter = useRef(2)
  const nextTempId = (prefix) => `${prefix}-${tempIdCounter.current++}`

  // ---- Load cards for the active site
  useEffect(() => {
    if (!activeCompanyId || isDemoMode) return
    setSelectedCardId(null)
    setItems([])
    setConsignments([newConsignment('c-1')])
    setStep(1)
    setMode('building')
    tempIdCounter.current = 2
    supabase.from('companies').select('id, name, contact_name, contact_email, delivery_address, delivery_postcode, client_groups(consignments_enabled)').eq('id', activeCompanyId).maybeSingle()
      .then(({ data }) => setCompany(data))
    supabase.from('cards').select('id, name, notes').eq('company_id', activeCompanyId).is('archived_at', null).order('name')
      .then(({ data }) => setCards(data || []))
    // Recent past orders for the "duplicate previous" panel.
    supabase.from('orders')
      .select('id, reference, status, placed_at, customer_po, created_at, card_id')
      .eq('company_id', activeCompanyId)
      .neq('status', 'draft')
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => setPastOrders(data || []))
  }, [activeCompanyId])

  // ---- When a card is picked, load its products
  useEffect(() => {
    if (!selectedCardId || isDemoMode) { setItems([]); return }
    supabase.from('card_products')
      .select('id, overrides, default_qty, sort_order, products(id, sku, name, description, unit, pack_size, price, image_url)')
      .eq('card_id', selectedCardId)
      .order('sort_order')
      .then(({ data }) => setItems(data || []))
  }, [selectedCardId])

  // ---- Consignment helpers
  const updateConsignment = (tempId, patch) =>
    setConsignments(cs => cs.map(c => c.tempId === tempId ? { ...c, ...patch } : c))

  const addConsignment = () =>
    setConsignments(cs => [...cs, newConsignment(nextTempId('c'))])

  const removeConsignment = (tempId) => {
    if (consignments.length === 1) {
      // Don't remove the last one -- just clear it.
      setConsignments([newConsignment('c-1')])
      tempIdCounter.current = 2
      return
    }
    if (!confirm('Remove this consignment?')) return
    setConsignments(cs => cs.filter(c => c.tempId !== tempId))
  }

  const addLineToConsignment = (consTempId, cardProduct) => {
    const snap = snapshotFromCardProduct(cardProduct)
    const lineTempId = nextTempId('l')
    setConsignments(cs => cs.map(c => c.tempId === consTempId ? {
      ...c,
      lines: [...c.lines, { tempId: lineTempId, ...snap, qty: 1 }],
    } : c))
  }

  const updateLine = (consTempId, lineTempId, patch) =>
    setConsignments(cs => cs.map(c => c.tempId !== consTempId ? c : {
      ...c,
      lines: c.lines.map(l => l.tempId === lineTempId ? { ...l, ...patch } : l),
    }))

  const removeLine = (consTempId, lineTempId) =>
    setConsignments(cs => cs.map(c => c.tempId !== consTempId ? c : {
      ...c,
      lines: c.lines.filter(l => l.tempId !== lineTempId),
    }))

  // ---- Derived totals + validity
  const allLines = useMemo(
    () => consignments.flatMap(c => c.lines.filter(l => Number(l.qty) > 0)),
    [consignments]
  )
  const total = allLines.reduce((s, l) => s + Number(l.price_at_order || 0) * Number(l.qty || 0), 0)
  const canSubmit = allLines.length > 0
  const poProvided = customerPo.trim().length > 0
  const canReview = canSubmit && poProvided

  // ---- Product picker candidates (filter by search, hide already-added lines for that consignment)
  const pickerCandidates = useMemo(() => {
    if (!pickingForId) return []
    const cons = consignments.find(c => c.tempId === pickingForId)
    if (!cons) return []
    const alreadyAdded = new Set(cons.lines.map(l => l.card_product_id))
    const search = pickerSearch.toLowerCase()
    return items
      .filter(cp => !alreadyAdded.has(cp.id))
      .filter(cp => {
        if (!search) return true
        const p = cp.products || {}
        return (effective(cp, 'name') || '').toLowerCase().includes(search) ||
               (p.sku || '').toLowerCase().includes(search)
      })
  }, [pickingForId, pickerSearch, items, consignments])

  // ---- Duplicate a previous order into the wizard state
  const duplicateFromPrevious = async (order) => {
    if (isDemoMode) return
    setLoadingPrev(true)
    try {
      // Load the source order's consignments + lines in parallel.
      const [consRes, linesRes] = await Promise.all([
        supabase.from('order_consignments')
          .select('id, recipient_name, recipient_ref, sort_order')
          .eq('order_id', order.id).order('sort_order', { ascending: true }),
        supabase.from('order_lines')
          .select('product_id, name_at_order, description_at_order, sku_at_order, unit_at_order, pack_size_at_order, price_at_order, qty, consignment_id')
          .eq('order_id', order.id).order('line_number', { ascending: true }),
      ])
      const consRows = consRes.data || []
      const lineRows = linesRes.data || []

      // Look up image_url from the current products list where we can — the
      // stored line snapshot doesn't include it.
      const productImages = new Map()
      if (order.card_id) {
        const { data: cpRows } = await supabase.from('card_products')
          .select('product_id, products(image_url)')
          .eq('card_id', order.card_id)
        for (const cp of cpRows || []) {
          if (cp.product_id && cp.products?.image_url) productImages.set(cp.product_id, cp.products.image_url)
        }
      }

      // Build wizard consignments. If the group has consignments disabled,
      // dump every line into a single default consignment.
      const groupHasConsignments = company?.client_groups?.consignments_enabled !== false
      let nextTemp = 2
      tempIdCounter.current = 2
      let newConsignments
      if (groupHasConsignments && consRows.length > 0) {
        const idMap = new Map()
        newConsignments = consRows.map((c, i) => {
          const tempId = `c-${nextTemp++}`
          idMap.set(c.id, tempId)
          return {
            tempId,
            recipient_name: c.recipient_name || '',
            recipient_ref: c.recipient_ref || '',
            lines: [],
          }
        })
        for (const l of lineRows) {
          const consTempId = l.consignment_id ? idMap.get(l.consignment_id) : newConsignments[0]?.tempId
          const target = newConsignments.find(c => c.tempId === consTempId) || newConsignments[0]
          if (!target) continue
          target.lines.push({
            tempId: `l-${nextTemp++}`,
            card_product_id: null, // not tracked on order_lines
            product_id: l.product_id,
            sku_at_order: l.sku_at_order,
            name_at_order: l.name_at_order,
            description_at_order: l.description_at_order,
            unit_at_order: l.unit_at_order,
            pack_size_at_order: l.pack_size_at_order,
            price_at_order: Number(l.price_at_order || 0),
            image_url: l.product_id ? productImages.get(l.product_id) : null,
            qty: Number(l.qty || 1),
          })
        }
        // Ensure at least one exists.
        if (newConsignments.length === 0) newConsignments = [newConsignment('c-1')]
      } else {
        const single = { tempId: 'c-1', recipient_name: '', recipient_ref: '', lines: [] }
        for (const l of lineRows) {
          single.lines.push({
            tempId: `l-${nextTemp++}`,
            card_product_id: null,
            product_id: l.product_id,
            sku_at_order: l.sku_at_order,
            name_at_order: l.name_at_order,
            description_at_order: l.description_at_order,
            unit_at_order: l.unit_at_order,
            pack_size_at_order: l.pack_size_at_order,
            price_at_order: Number(l.price_at_order || 0),
            image_url: l.product_id ? productImages.get(l.product_id) : null,
            qty: Number(l.qty || 1),
          })
        }
        newConsignments = [single]
      }
      tempIdCounter.current = nextTemp

      // Load the source card so the "Add another product" picker works.
      if (order.card_id) setSelectedCardId(order.card_id)
      setConsignments(newConsignments)
      // Clear PO — user should enter a fresh reference for the new order.
      setCustomerPo('')
      setDeliveryDate('')
      setClientNotes('')
      // Advance to step 2. Duplicate goToStep behaviour inline since it's
      // defined below in the render path.
      setStep(2)
      setMode('building')
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      alert(err.message || 'Could not load that order')
    } finally {
      setLoadingPrev(false)
    }
  }

  // ---- Submit
  const placeOrder = async (status) => {
    if (isDemoMode) return
    if (previewMode) return alert('Preview mode — submission is disabled.')
    if (!canSubmit) return alert('Add at least one product before submitting.')

    const setter = status === 'sent' ? setSubmitting : setSavingDraft
    setter(true)
    try {
      // 1. Create the order
      const orderPayload = {
        company_id: activeCompanyId,
        card_id: selectedCardId,
        status,
        delivery_address: company?.delivery_address || null,
        delivery_postcode: company?.delivery_postcode || null,
        delivery_date_requested: deliveryDate || null,
        customer_po: customerPo || null,
        client_notes: clientNotes || null,
        placed_at: status === 'sent' ? new Date().toISOString() : null,
      }
      const { data: newOrder, error: oErr } = await supabase.from('orders').insert(orderPayload).select().single()
      if (oErr) throw oErr

      // 2. For each consignment with at least one line, insert a consignment + its lines.
      let lineNumber = 0
      for (let i = 0; i < consignments.length; i++) {
        const c = consignments[i]
        const validLines = c.lines.filter(l => Number(l.qty) > 0)
        if (validLines.length === 0) continue

        const { data: cons, error: cErr } = await supabase
          .from('order_consignments')
          .insert({
            order_id: newOrder.id,
            recipient_name: c.recipient_name.trim() || null,
            recipient_ref: c.recipient_ref.trim() || null,
            sort_order: i,
          })
          .select()
          .single()
        if (cErr) throw cErr

        const linesPayload = validLines.map(l => {
          lineNumber += 1
          return {
            order_id: newOrder.id,
            consignment_id: cons.id,
            product_id: l.product_id || null,
            line_number: lineNumber,
            sku_at_order: l.sku_at_order,
            name_at_order: l.name_at_order,
            description_at_order: l.description_at_order,
            unit_at_order: l.unit_at_order,
            pack_size_at_order: l.pack_size_at_order,
            price_at_order: Number(l.price_at_order || 0),
            qty: Number(l.qty || 0),
            // Also denormalise onto the line so older consumers still see it.
            recipient_name: c.recipient_name.trim() || null,
            recipient_ref: c.recipient_ref.trim() || null,
          }
        })
        const { error: lErr } = await supabase.from('order_lines').insert(linesPayload)
        if (lErr) throw lErr
      }

      // 3. Notification email (best-effort). Branches on the status the
      //    BEFORE INSERT trigger may have flipped us to.
      const finalStatus = newOrder.status
      if (finalStatus === 'awaiting_approval' && newOrder.approval_token) {
        // Approval-required group: email the approver, NOT WG.
        try {
          const { data: grp } = await supabase
            .from('client_groups')
            .select('name, approver_email, approver_name')
            .eq('id', company?.client_group_id)
            .maybeSingle()
          const approveUrl = `${window.location.origin}/approve/${newOrder.approval_token}`
          const approverName = grp?.approver_name?.trim() || 'there'
          const siteName = company?.name || 'a site'
          const approverHtml = `
            <p>Hi ${approverName},</p>
            <p><strong>${siteName}</strong> has placed order <strong>${newOrder.reference}</strong> and it's waiting for your approval before it's sent to Walter Geering.</p>
            <p><a href="${approveUrl}" style="display:inline-block;background:#0a5842;color:#fff;padding:12px 20px;border-radius:8px;text-decoration:none;font-weight:600">Review &amp; approve this order</a></p>
            <p style="color:#666;font-size:13px">This link is valid for 7 days and can only be used once. If you didn't expect this order, click through and reject it — the site placer will be notified.</p>
          `
          if (grp?.approver_email) {
            await supabase.functions.invoke('send-order-email', {
              body: {
                order_id: newOrder.id,
                client: {
                  to: grp.approver_email,
                  subject: `Order ${newOrder.reference} from ${siteName} — awaiting your approval`,
                  html: approverHtml,
                },
                internal: null,
              },
            })
          }
        } catch (_) {
          // Approver won't get an email but order is recorded; head office can find it in Admin.
        }
      } else if (finalStatus === 'sent') {
        try {
          await supabase.functions.invoke('send-order-email', {
            body: {
              order_id: newOrder.id,
              client: null,
              internal: {
                subject: `New order ${newOrder.reference} from ${company?.name || 'a client'}`,
                html: `<p>${company?.name || 'A client'} just placed order <strong>${newOrder.reference}</strong> via the portal. <a href="${window.location.origin}/orders/${newOrder.id}">Open it in admin</a>.</p>`,
              },
            },
          })
        } catch (_) {
          // Function not deployed yet -- order is recorded regardless.
        }
      }

      if (finalStatus === 'awaiting_approval') {
        alert(`Order ${newOrder.reference} has been sent to your head office for approval. You'll be notified when it's approved.`)
      }
      navigate('/my-orders')
    } catch (err) {
      alert(err.message)
    } finally {
      setter(false)
    }
  }

  if (!activeCompanyId) {
    return (
      <div className="card max-w-2xl">
        <h3 className="font-semibold text-gray-800 mb-2">Pick a specific site first</h3>
        <p className="text-sm text-gray-600 mb-3">
          You're currently viewing <strong>All sites</strong> combined — great for reports and history,
          but orders always belong to one specific site. Use the site picker in the sidebar to
          choose which site you'd like to place an order for.
        </p>
        <p className="text-xs text-gray-500">
          Once you pick a site, this page will load its available order cards and let you build the order.
        </p>
      </div>
    )
  }

  const consignmentsEnabled = company?.client_groups?.consignments_enabled !== false
  const goToStep = (n) => {
    setStep(n)
    setMode(n === 4 ? 'review' : 'building')
    // scroll to top so the client sees the new step's card immediately
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Guards: can they advance from step N?
  const canAdvanceFromProducts = canSubmit
  const canAdvanceFromDetails = poProvided

  const STEPS = [
    { n: 1, label: 'Choose card' },
    { n: 2, label: 'Add products' },
    { n: 3, label: 'Delivery details' },
    { n: 4, label: 'Review & send' },
  ]

  return (
    <div className="space-y-4">
      <DemoNotice />

      {/* Step indicator — always visible so client sees where they are */}
      <div className="card !p-4">
        <ol className="flex items-center gap-2 sm:gap-4 flex-wrap">
          {STEPS.map((s, i) => {
            const isActive = s.n === step
            const isDone = s.n < step
            return (
              <li key={s.n} className="flex items-center gap-2 flex-1 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
                  isActive ? 'bg-brand text-white' :
                  isDone   ? 'bg-brand-100 text-brand-dark' :
                             'bg-gray-100 text-gray-400'
                }`}>
                  {isDone ? <Check className="w-4 h-4" /> : s.n}
                </div>
                <div className={`text-sm truncate ${
                  isActive ? 'font-semibold text-gray-800' :
                  isDone   ? 'text-gray-600' :
                             'text-gray-400'
                }`}>{s.label}</div>
                {i < STEPS.length - 1 && (
                  <div className={`hidden sm:block flex-1 h-0.5 mx-2 ${isDone ? 'bg-brand-100' : 'bg-gray-100'}`} />
                )}
              </li>
            )
          })}
        </ol>
      </div>

      {/* ============ STEP 1: Choose a card or duplicate a previous order ============ */}
      {step === 1 && (
        <>
          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-1 text-lg">Step 1 — How would you like to start?</h3>
            <p className="text-sm text-gray-500 mb-4">
              Start fresh with a product card below, or copy a previous order and tweak the details.
            </p>

            {/* Start fresh — cards */}
            <div className="mb-2">
              <div className="section-label mb-2">Start a new order</div>
              {cards.length === 0
                ? <p className="text-sm text-gray-500 italic">No cards set up for this site yet. Ask your account manager to add products.</p>
                : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {cards.map(c => (
                      <button
                        key={c.id}
                        onClick={() => { setSelectedCardId(c.id); goToStep(2) }}
                        className="text-left p-4 rounded-xl border border-gray-200 bg-white hover:border-brand hover:shadow-sm transition-all flex items-center gap-3 group"
                      >
                        <ClipboardList className="w-6 h-6 text-brand" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">{c.name}</div>
                          {c.notes && <div className="text-xs text-gray-500 mt-0.5">{c.notes}</div>}
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-brand transition" />
                      </button>
                    ))}
                  </div>
                )
              }
            </div>
          </div>

          {/* Duplicate a previous order — always shown so the option is
              discoverable. If there are no past orders yet, we render a
              friendly empty state instead of hiding the panel. */}
          <div className="card">
            <div className="section-label mb-2 flex items-center gap-2">
              <Repeat className="w-3.5 h-3.5" /> Or duplicate a previous order
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Same products, quantities and recipients as before — quick when you order the same set regularly.
              You can tweak anything before submitting.
            </p>
            {pastOrders.length === 0 && !loadingPrev && (
              <p className="text-sm text-gray-500 italic py-3 px-4 bg-gray-50 rounded-lg">
                No previous orders yet. Once you've placed one, it'll appear here so you can copy it in one click.
              </p>
            )}
            {pastOrders.length > 0 && (
              loadingPrev
                ? <p className="text-sm text-gray-500 italic">Loading previous order…</p>
                : (
                  <ul className="divide-y divide-gray-100">
                    {pastOrders.map(o => (
                      <li key={o.id}>
                        <button
                          onClick={() => duplicateFromPrevious(o)}
                          disabled={loadingPrev}
                          className="w-full text-left py-3 px-2 -mx-2 rounded-lg hover:bg-gray-50 flex items-center gap-3 transition-colors group"
                        >
                          <Repeat className="w-5 h-5 text-brand shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800 truncate">
                              {o.reference || '(draft)'}
                              {o.customer_po && <span className="text-gray-400 font-normal ml-2">· PO {o.customer_po}</span>}
                            </div>
                            <div className="text-xs text-gray-500 truncate flex items-center gap-1.5">
                              <Calendar className="w-3 h-3" />
                              {o.placed_at ? new Date(o.placed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : new Date(o.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              <span className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-[10px] uppercase text-gray-600">{o.status}</span>
                            </div>
                          </div>
                          <span className="text-xs text-brand-dark font-medium opacity-70 group-hover:opacity-100 inline-flex items-center gap-1">
                            Copy to new order <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )
            )}
          </div>
        </>
      )}

      {/* ============ STEP 2: Add products / consignments ============ */}
      {step === 2 && (
        <div className="card">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <h3 className="font-semibold text-gray-800 text-lg">Step 2 — Add your products</h3>
            <button onClick={() => goToStep(1)} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Change card
            </button>
          </div>
          <p className="text-sm text-gray-500">
            {consignmentsEnabled
              ? 'Add products for each recipient. Use "New consignment" if you\'re ordering for more than one person on this order.'
              : 'Add the products you want, set the quantities, then continue.'
            }
          </p>
        </div>
      )}

      {step >= 2 && (
        <>
          {step === 3 && (
            <div className="card">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h3 className="font-semibold text-gray-800 text-lg">Step 3 — Delivery details</h3>
                <button onClick={() => goToStep(2)} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to products
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Enter your PO reference (required), your preferred delivery date, and any notes for the team.
              </p>
            </div>
          )}
          {step === 4 && (
            <div className="card">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h3 className="font-semibold text-gray-800 text-lg">Step 4 — Review &amp; send</h3>
                <button onClick={() => goToStep(3)} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to details
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Check everything's right below, then click <strong>Send order</strong>. You'll get a confirmation email straight away.
              </p>
            </div>
          )}

          {(step === 3 || step === 4) && (
          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-3">Order details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label>
                <span className="text-xs text-gray-500 block mb-1">
                  Customer PO / your reference <span className="text-red-600">*</span>
                </span>
                {mode === 'review'
                  ? <div className="font-medium text-gray-800">{customerPo}</div>
                  : <input
                      type="text" value={customerPo}
                      onChange={e => setCustomerPo(e.target.value)}
                      placeholder="Required — your internal reference"
                      className={`input-field ${!poProvided ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : ''}`}
                    />
                }
                {mode === 'building' && !poProvided && (
                  <p className="text-xs text-red-600 mt-1">PO number must be filled in before you can review &amp; submit.</p>
                )}
              </label>
              <label>
                <span className="text-xs text-gray-500 block mb-1">Requested delivery date</span>
                {mode === 'review'
                  ? <div className="font-medium text-gray-800">{deliveryDate || <span className="text-gray-400 italic">none</span>}</div>
                  : <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="input-field" />
                }
              </label>
              <label className="sm:col-span-2">
                <span className="text-xs text-gray-500 block mb-1">Notes (optional)</span>
                {mode === 'review'
                  ? <div className="text-gray-800 whitespace-pre-wrap">{clientNotes || <span className="text-gray-400 italic">none</span>}</div>
                  : <textarea value={clientNotes} onChange={e => setClientNotes(e.target.value)} className="input-field" rows={2} />
                }
              </label>
            </div>
            {(company?.delivery_address || company?.delivery_postcode) && (
              <p className="text-xs text-gray-500 mt-3">
                Delivery to: {company.delivery_address}{company.delivery_postcode ? ', ' + company.delivery_postcode : ''}
              </p>
            )}
          </div>
          )}

          {(step === 2 || step === 4) && (() => {
            return consignments.map((cons, idx) => {
            const consTotal = cons.lines.reduce((s, l) => s + Number(l.price_at_order || 0) * Number(l.qty || 0), 0)
            const isPicking = pickingForId === cons.tempId
            const productCount = cons.lines.filter(l => Number(l.qty) > 0).length
            return (
              <div key={cons.tempId} className="card">
                {consignmentsEnabled && (
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                      <User className="w-5 h-5 text-brand" />
                      Consignment {idx + 1}
                      {productCount > 0 && (
                        <span className="text-sm font-normal text-gray-500">
                          ({productCount} product{productCount === 1 ? '' : 's'})
                        </span>
                      )}
                    </h3>
                    {mode === 'building' && consignments.length > 1 && (
                      <button
                        onClick={() => removeConsignment(cons.tempId)}
                        className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" /> Remove
                      </button>
                    )}
                  </div>
                )}

                {consignmentsEnabled && (mode === 'review' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Hamper for?</span>
                      <div className="font-medium text-gray-800">
                        {cons.recipient_name || <span className="text-gray-400 italic">not set</span>}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">No.</span>
                      <div className="font-medium text-gray-800">
                        {cons.recipient_ref || <span className="text-gray-400 italic">not set</span>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3 mb-4">
                    <label>
                      <span className="text-xs text-gray-500 block mb-1">Hamper for?</span>
                      <input
                        type="text"
                        value={cons.recipient_name}
                        onChange={e => updateConsignment(cons.tempId, { recipient_name: e.target.value })}
                        placeholder="Recipient name"
                        className="input-field"
                      />
                    </label>
                    <label>
                      <span className="text-xs text-gray-500 block mb-1">No.</span>
                      <input
                        type="text"
                        value={cons.recipient_ref}
                        onChange={e => updateConsignment(cons.tempId, { recipient_ref: e.target.value })}
                        placeholder="e.g. lodge / plot"
                        className="input-field"
                      />
                    </label>
                  </div>
                ))}

                {cons.lines.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No products yet — use the button below to add some.</p>
                ) : (
                  <ul className="divide-y divide-gray-100 mb-3">
                    {cons.lines.map(l => (
                      <li key={l.tempId} className="py-3 flex items-center gap-3">
                        {l.image_url
                          ? <img src={l.image_url} alt="" className="w-12 h-12 rounded-md object-cover bg-gray-100" />
                          : <div className="w-12 h-12 rounded-md bg-brand-50 flex items-center justify-center"><Package className="w-5 h-5 text-brand" /></div>
                        }
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-800 truncate">{l.name_at_order}</div>
                          <div className="text-xs text-gray-400">
                            {l.sku_at_order}
                            {l.unit_at_order ? ` · ${l.unit_at_order}` : ''}
                            {l.pack_size_at_order ? ` · ${l.pack_size_at_order}/box` : ''}
                            {l.price_at_order != null ? ` · £${Number(l.price_at_order).toFixed(2)}` : ''}
                          </div>
                        </div>
                        {mode === 'review' ? (
                          <div className="text-right">
                            <div className="font-medium text-gray-800">× {Number(l.qty)}</div>
                            <div className="text-xs text-gray-500">£{(Number(l.price_at_order) * Number(l.qty)).toFixed(2)}</div>
                          </div>
                        ) : (
                          <>
                            <input
                              type="number" min="1" step="1"
                              value={l.qty}
                              onChange={e => {
                                const v = e.target.value
                                updateLine(cons.tempId, l.tempId, { qty: v === '' ? '' : Math.max(1, parseInt(v, 10) || 1) })
                              }}
                              className="input-field w-20 text-right"
                            />
                            <button onClick={() => removeLine(cons.tempId, l.tempId)} className="text-gray-400 hover:text-red-600 p-1">
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {mode === 'building' && (isPicking ? (
                  <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                    <input
                      type="text" value={pickerSearch}
                      onChange={e => setPickerSearch(e.target.value)}
                      placeholder="Search products on this card…"
                      className="input-field"
                      autoFocus
                    />
                    {pickerCandidates.length === 0
                      ? <p className="text-sm text-gray-500 py-2">{items.length === 0 ? 'No products on this card.' : 'All products already added to this consignment, or no matches.'}</p>
                      : (
                        <ul className="divide-y divide-gray-100 bg-white rounded-lg border border-gray-100 max-h-[36rem] overflow-auto">
                          {pickerCandidates.map(cp => {
                            const p = cp.products || {}
                            const price = effective(cp, 'price')
                            const image = effective(cp, 'image_url') || p.image_url
                            const description = effective(cp, 'description') || p.description
                            return (
                              <li key={cp.id}>
                                <button
                                  type="button"
                                  onClick={() => { addLineToConsignment(cons.tempId, cp); setPickingForId(null); setPickerSearch('') }}
                                  className="w-full flex items-start gap-3 px-3 py-3 hover:bg-gray-50 text-left"
                                >
                                  {image
                                    ? <img src={image} alt="" className="w-20 h-20 rounded-md object-contain bg-gray-50 p-1 shrink-0 border border-gray-100" />
                                    : <div className="w-20 h-20 rounded-md bg-gray-50 flex items-center justify-center shrink-0 border border-gray-100"><Package className="w-8 h-8 text-gray-300" /></div>
                                  }
                                  <div className="flex-1 min-w-0 pt-0.5">
                                    <div className="text-sm font-semibold text-gray-800">{effective(cp, 'name')}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {p.sku}{effective(cp, 'pack_size') ? ` · ${effective(cp, 'pack_size')}/box` : ''}
                                    </div>
                                    {description && (
                                      <p className="text-xs text-gray-600 mt-1.5 leading-relaxed line-clamp-2">{description}</p>
                                    )}
                                  </div>
                                  {price != null && <div className="text-sm font-semibold text-gray-800 tabular-nums shrink-0 pt-0.5">£{Number(price).toFixed(2)}</div>}
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      )
                    }
                    <button onClick={() => { setPickingForId(null); setPickerSearch('') }} className="text-sm text-gray-500 hover:text-gray-700">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button onClick={() => { setPickingForId(cons.tempId); setPickerSearch('') }} className="btn-secondary text-sm">
                    <Plus className="w-4 h-4" /> {cons.lines.length === 0 ? 'Add product' : 'Add another product'}
                  </button>
                ))}

                {cons.lines.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-right text-gray-600">
                    Subtotal: <strong className="text-gray-800">£{consTotal.toFixed(2)}</strong>
                  </div>
                )}
              </div>
            )
          })
          })()}

          {step === 2 && consignmentsEnabled && (
            <button onClick={addConsignment} className="btn-secondary">
              <Plus className="w-4 h-4" /> New consignment (different recipient)
            </button>
          )}

          {/* ============ FOOTER: totals + action buttons per step ============ */}
          <div className="card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Order total</div>
              <div className="text-2xl font-semibold text-gray-800">£{total.toFixed(2)}</div>
              <div className="text-xs text-gray-500">
                {consignmentsEnabled && `${consignments.filter(c => c.lines.some(l => Number(l.qty) > 0)).length} consignment(s), `}
                {allLines.length} line(s)
              </div>
              {step === 3 && !poProvided && (
                <p className="text-xs text-red-600 mt-2">PO number required before you can review.</p>
              )}
              {step === 4 && (
                <p className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Ready to send. Click <strong>Send order</strong> below to submit.
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              {step === 2 && (
                <button
                  onClick={() => goToStep(3)}
                  disabled={!canAdvanceFromProducts || previewMode}
                  className="btn-primary"
                  title={previewMode ? 'Disabled in preview' : !canAdvanceFromProducts ? 'Add at least one product' : ''}
                >
                  Continue to delivery details <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {step === 3 && (
                <>
                  <button
                    onClick={() => placeOrder('draft')}
                    disabled={savingDraft || submitting || !canSubmit || previewMode}
                    className="btn-secondary"
                    title={previewMode ? 'Disabled in preview' : ''}
                  >
                    <Save className="w-4 h-4" /> {savingDraft ? 'Saving…' : 'Save as draft'}
                  </button>
                  <button
                    onClick={() => goToStep(4)}
                    disabled={!canAdvanceFromDetails || previewMode}
                    className="btn-primary"
                    title={previewMode ? 'Disabled in preview' : !poProvided ? 'Add a PO number first' : ''}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Review order
                  </button>
                </>
              )}
              {step === 4 && (
                <button
                  onClick={() => placeOrder('sent')}
                  disabled={submitting || previewMode}
                  className="btn-primary"
                  title={previewMode ? 'Disabled in preview' : ''}
                >
                  <Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Send order'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
